import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Stage, Layer, Image as KImage, Transformer } from 'react-konva';
import { Button } from '../../ui/button';
import { Play, Pause, SkipBack, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { substituteText } from '../../../utils/propertySubstitution';

const DESIGN_W = 1080;
const DESIGN_H = 1920;
const ASPECT = DESIGN_W / DESIGN_H; // 0.5625 (portrait 9:16)
// Height reserved below the canvas for the controls row (+ optional zoom bar).
const CONTROLS_RESERVE = 96;
const MIN_CANVAS_H = 120;
const DEFAULT_CANVAS_H = 400;

/* ── helpers ─────────────────────────────────────────────── */

function resolvePlaceholders(text, propertyData) {
  if (!text || !propertyData) return text;
  // Delegate to the shared util so the reel preview, carousel/canvas, and the
  // backend render all resolve the SAME placeholders (incl. street/city/state).
  return substituteText(text, propertyData);
}

function parseBgColor(bgColor, opacity) {
  if (!bgColor || bgColor === '') return {};
  const parts = bgColor.split('@');
  const color = parts[0];
  const alpha = parts[1] ? parseFloat(parts[1]) * opacity : opacity;
  let rgba;
  if (color === 'black') rgba = `rgba(0,0,0,${alpha})`;
  else if (color === 'white') rgba = `rgba(255,255,255,${alpha})`;
  else if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    rgba = `rgba(${r},${g},${b},${alpha})`;
  } else rgba = `rgba(0,0,0,${alpha})`;
  return { backgroundColor: rgba, padding: '1px 4px', borderRadius: '2px' };
}

/* ── useKonvaImage hook ──────────────────────────────────── */

function useKonvaImage(url) {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!url) { setImage(null); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = url;
    return () => { img.onload = null; img.onerror = null; };
  }, [url]);
  return image;
}

/* ── Interactive Layer (HTML — text / shapes) ────────────── */

const InteractiveLayer = ({
  layer, isSelected, onSelect, onUpdate, onUpdateLive, onGestureStart, propertyData, isPlaying, sx, sy,
}) => {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [editing, setEditing] = useState(false);
  const dragStartRef = useRef(null);
  const lastTapRef = useRef(0);
  const inputRef = useRef(null);

  const x = (layer.x || 0) * sx;
  const y = (layer.y || 0) * sy;
  const w = (layer.width || 100) * sx;
  const h = (layer.height || 100) * sy;
  const opacity = layer.opacity ?? 1;
  const rotation = layer.rotation || 0;
  const displayContent = layer.type === 'text' ? resolvePlaceholders(layer.content, propertyData) : layer.content;

  // Live update during a gesture (no history); falls back to onUpdate.
  const live = onUpdateLive || onUpdate || (() => {});

  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const handlePointerDown = (e) => {
    if (isPlaying || editing) return;
    e.stopPropagation();
    onSelect(layer.id);
    setDragging(true);
    dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, layerX: layer.x, layerY: layer.y };
    if (e.target.setPointerCapture && e.pointerId != null) {
      try { e.target.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
  };

  const handleResizeStart = (e, corner) => {
    if (isPlaying) return;
    e.stopPropagation(); e.preventDefault();
    setResizing(corner);
    dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, layerX: layer.x, layerY: layer.y, layerW: layer.width, layerH: layer.height };
    if (e.target.setPointerCapture && e.pointerId != null) {
      try { e.target.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
  };

  // Rotation handle — drags an arc relative to the layer's center.
  const handleRotateStart = (e) => {
    if (isPlaying) return;
    e.stopPropagation(); e.preventDefault();
    setRotating(true);
    const parent = e.currentTarget.closest('[data-canvas-root]');
    const pr = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
    const cx = pr.left + x + w / 2;
    const cy = pr.top + y + h / 2;
    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    dragStartRef.current = { centerX: cx, centerY: cy, startAngle, startRotation: rotation };
    if (e.target.setPointerCapture && e.pointerId != null) {
      try { e.target.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
  };

  useEffect(() => {
    if (!dragging && !resizing && !rotating) return;
    // Snapshot exactly once per gesture — on the first actual move — so one
    // drag/resize/rotate = one undo step (not ~60 history entries).
    let snapped = false;
    const ensureSnapshot = () => { if (!snapped) { snapped = true; if (onGestureStart) onGestureStart(); } };
    const handleMove = (e) => {
      const ds = dragStartRef.current;
      if (!ds) return;
      if (dragging) {
        const dx = (e.clientX - ds.mouseX) / sx;
        const dy = (e.clientY - ds.mouseY) / sy;
        ensureSnapshot();
        live(layer.id, { x: Math.round(ds.layerX + dx), y: Math.round(ds.layerY + dy) });
      } else if (resizing) {
        const dx = (e.clientX - ds.mouseX) / sx;
        const dy = (e.clientY - ds.mouseY) / sy;
        let newX = ds.layerX, newY = ds.layerY, newW = ds.layerW, newH = ds.layerH;
        if (resizing.includes('right')) newW = Math.max(20, ds.layerW + dx);
        if (resizing.includes('bottom')) newH = Math.max(20, ds.layerH + dy);
        if (resizing.includes('left')) { newW = Math.max(20, ds.layerW - dx); newX = ds.layerX + dx; }
        if (resizing.includes('top')) { newH = Math.max(20, ds.layerH - dy); newY = ds.layerY + dy; }
        ensureSnapshot();
        live(layer.id, { x: Math.round(newX), y: Math.round(newY), width: Math.round(newW), height: Math.round(newH) });
      } else if (rotating) {
        const currentAngle = Math.atan2(e.clientY - ds.centerY, e.clientX - ds.centerX) * 180 / Math.PI;
        const delta = currentAngle - ds.startAngle;
        let next = ds.startRotation + delta;
        if (e.shiftKey) next = Math.round(next / 15) * 15;
        ensureSnapshot();
        live(layer.id, { rotation: Math.round(next) % 360 });
      }
    };
    const handleUp = () => { setDragging(false); setResizing(null); setRotating(false); };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [dragging, resizing, rotating, layer.id, live, onGestureStart, sx, sy]);

  const handlePointerUp = () => {
    if (layer.type !== 'text' || isPlaying) return;
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      setEditing(true);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };
  const commitEdit = (val) => { if (onUpdate) onUpdate(layer.id, { content: val }); setEditing(false); };

  const baseStyle = {
    position: 'absolute', left: x, top: y, width: w, height: h, opacity,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    cursor: isPlaying ? 'default' : (dragging ? 'grabbing' : 'grab'),
    zIndex: isSelected ? 20 : 10,
    pointerEvents: isPlaying ? 'none' : 'auto',
    touchAction: isPlaying ? 'auto' : 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  const handleSize = 12;
  const rotationKnobOffset = 28;
  const handles = isSelected && !isPlaying ? (
    <>
      {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(corner => {
        const isTop = corner.includes('top'), isLeft = corner.includes('left');
        return (
          <div key={corner} className="absolute bg-white border-2 border-purple-500 rounded-sm z-30"
            style={{
              width: handleSize, height: handleSize,
              top: isTop ? -handleSize / 2 : undefined, bottom: !isTop ? -handleSize / 2 : undefined,
              left: isLeft ? -handleSize / 2 : undefined, right: !isLeft ? -handleSize / 2 : undefined,
              cursor: `${corner.replace('-', '')}-resize`,
              touchAction: 'none',
            }}
            onPointerDown={(e) => handleResizeStart(e, corner)}
            data-testid={`resize-${corner}`}
          />
        );
      })}
      <div
        className="absolute z-30 flex items-center justify-center"
        style={{ left: '50%', top: -rotationKnobOffset, transform: 'translateX(-50%)', touchAction: 'none' }}
        onPointerDown={handleRotateStart}
        data-testid="rotate-handle"
      >
        <div className="w-px bg-purple-500" style={{ height: rotationKnobOffset - 12, position: 'absolute', top: 12 }} />
        <div className="w-5 h-5 rounded-full bg-white border-2 border-purple-500 shadow-md cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'none' }} />
      </div>
    </>
  ) : null;

  if (layer.type === 'rect') {
    const rectStyle = {
      ...baseStyle,
      backgroundColor: layer.fill || '#8B5CF6',
      borderRadius: `${(layer.borderRadius || 0) * sx}px`,
    };
    if (layer.stroke && layer.strokeWidth > 0) {
      rectStyle.border = `${layer.strokeWidth * sx}px solid ${layer.stroke}`;
      rectStyle.boxSizing = 'border-box';
    }
    return (
      <div style={rectStyle}
        className={isSelected ? 'ring-1 ring-purple-500 ring-offset-0' : ''}
        onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} data-testid={`canvas-layer-${layer.id}`}>{handles}</div>
    );
  }
  if (layer.type === 'circle') {
    return (
      <div style={{ ...baseStyle, borderRadius: '50%', backgroundColor: layer.fill || '#8B5CF6' }}
        className={isSelected ? 'ring-1 ring-purple-500 ring-offset-0' : ''}
        onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} data-testid={`canvas-layer-${layer.id}`}>{handles}</div>
    );
  }
  if (layer.type === 'text') {
    const fontSize = Math.max(1, (layer.fontSize || 48) * sx);
    const textAlign = layer.textAlign || 'center';
    const lineHeight = layer.lineHeight || 1.2;
    const fontFamily = layer.fontFamily || 'Montserrat, sans-serif';
    const fontWeight = layer.fontWeight || 'bold';
    const fontStyle = layer.fontStyle || 'normal';
    const textDecoration = layer.textDecoration || 'none';
    const textTransform = layer.textTransform || 'none';
    const shadowObj = layer.shadow || {};
    const bgHL = layer.bgHighlight || {};

    const textShadow = shadowObj.color
      ? `${shadowObj.offsetX || 0}px ${shadowObj.offsetY || 0}px ${shadowObj.blur || 0}px ${shadowObj.color}`
      : undefined;

    const hlStyle = bgHL.enabled ? {
      backgroundColor: bgHL.color ? `${bgHL.color}${Math.round((bgHL.opacity ?? 0.6) * 255).toString(16).padStart(2, '0')}` : 'rgba(0,0,0,0.6)',
      padding: `${(bgHL.padding || 12) * sx}px`,
      borderRadius: `${(bgHL.borderRadius || 0) * sx}px`,
    } : {};

    const bgStyle = bgHL.enabled ? hlStyle : parseBgColor(layer.bgColor, opacity);

    return (
      <div style={{ ...baseStyle, ...bgStyle, height: 'auto', minHeight: h, textAlign, display: 'flex', flexDirection: 'column', justifyContent: textAlign === 'center' ? 'center' : 'flex-start' }}
        className={`whitespace-pre-wrap ${isSelected ? 'ring-1 ring-purple-500 ring-offset-0' : ''}`}
        onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} data-testid={`canvas-layer-${layer.id}`}>
        {editing ? (
          <textarea ref={inputRef} defaultValue={layer.content || ''}
            className="bg-transparent outline-none w-full resize-none"
            style={{ fontSize: `${fontSize}px`, fontFamily, fontWeight, fontStyle, textDecoration, textTransform, color: layer.fontColor || 'white', textAlign, lineHeight, textShadow }}
            rows={3}
            onBlur={(e) => commitEdit(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
            data-testid="canvas-text-input" />
        ) : (
          <span style={{ fontSize: `${fontSize}px`, fontFamily, fontWeight, fontStyle, textDecoration, textTransform, color: layer.fontColor || 'white', lineHeight, textShadow, display: 'block', textAlign, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {displayContent || 'Text'}
          </span>
        )}
        {handles}
      </div>
    );
  }
  return null;
};

/* ── Konva Asset Image with Transformer ──────────────────── */

const AssetImage = ({ track, isPlaying, isSelected, onSelect, onTransformChange, canvasW, canvasH, sx, sy }) => {
  const image = useKonvaImage(track?.source_url);
  const imageRef = useRef(null);
  const trRef = useRef(null);

  const coverS = useMemo(() => {
    if (!image) return 1;
    return Math.max(canvasW / image.naturalWidth, canvasH / image.naturalHeight);
  }, [image, canvasW, canvasH]);

  const transform = track?.transform || {};
  const userScale = transform.scale || 1;
  const userX = transform.x || 0;
  const userY = transform.y || 0;
  const userRotation = transform.rotation || 0;

  useEffect(() => {
    const tr = trRef.current;
    const node = imageRef.current;
    if (!tr) return;
    if (isSelected && node) {
      tr.nodes([node]);
      tr.getLayer()?.batchDraw();
    } else {
      tr.nodes([]);
    }
  }, [isSelected, image]);

  if (!image) return null;

  const s = coverS * userScale;
  const x = canvasW / 2 + userX * sx;
  const y = canvasH / 2 + userY * sy;

  const syncTransform = () => {
    const node = imageRef.current;
    if (!node) return;
    onTransformChange({
      x: Math.round((node.x() - canvasW / 2) / sx),
      y: Math.round((node.y() - canvasH / 2) / sy),
      scale: parseFloat((node.scaleX() / coverS).toFixed(4)),
      rotation: Math.round(node.rotation() * 10) / 10,
    });
  };

  return (
    <>
      <KImage
        ref={imageRef}
        image={image}
        width={image.naturalWidth}
        height={image.naturalHeight}
        offsetX={image.naturalWidth / 2}
        offsetY={image.naturalHeight / 2}
        x={x} y={y}
        scaleX={s} scaleY={s}
        rotation={userRotation}
        draggable={!isPlaying && isSelected}
        onClick={(e) => { if (!isPlaying) { e.cancelBubble = true; onSelect(); } }}
        onTap={(e) => { if (!isPlaying) { e.cancelBubble = true; onSelect(); } }}
        onDragEnd={syncTransform}
        onTransformEnd={syncTransform}
      />
      <Transformer
        ref={trRef}
        keepRatio
        centeredScaling
        rotateEnabled
        borderStroke="#7c3aed"
        borderStrokeWidth={1.5}
        anchorFill="#7c3aed"
        anchorStroke="#ffffff"
        anchorSize={8}
        anchorCornerRadius={2}
        enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
        rotateAnchorOffset={20}
        visible={isSelected && !isPlaying}
      />
    </>
  );
};

/* ── PreviewPlayer ───────────────────────────────────────── */

export const PreviewPlayer = ({
  tracks, layers = [], overlays = [], onTimeUpdate, onPlayingChange,
  selectedLayerId, onSelectLayer, onUpdateLayer, onUpdateLayerLive, onLayerGestureStart,
  propertyData, onUpdateTrackTransform, seekTime, audioSegments = [], assets = [],
}) => {
  const [playing, setPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [assetSelected, setAssetSelected] = useState(false);
  const timerRef = useRef(null);
  const startWallRef = useRef(null);     // performance.now() when playback (re)started
  const startElapsedRef = useRef(0);     // elapsed value at that (re)start — enables resume
  const elapsedRef = useRef(0);
  const currentIdxRef = useRef(0);
  const lastUiRef = useRef(0);
  const videoRef = useRef(null);

  // Responsive canvas sizing — fits the available area while preserving 9:16.
  const rootRef = useRef(null);
  const [canvasH, setCanvasH] = useState(DEFAULT_CANVAS_H);
  const canvasW = Math.round(canvasH * ASPECT);
  const sx = canvasW / DESIGN_W;
  const sy = canvasH / DESIGN_H;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const recompute = () => {
      const rect = el.getBoundingClientRect();
      const availW = rect.width;
      const availH = rect.height;
      if (availW <= 0 || availH <= 0) return;
      let h = Math.min(availH - CONTROLS_RESERVE, availW / ASPECT);
      h = Math.max(MIN_CANVAS_H, Math.round(h));
      setCanvasH(prev => (Math.abs(prev - h) > 1 ? h : prev));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const allLayers = layers.length > 0 ? layers : overlays;
  const getEffDur = (t) => (t.duration || 3) / Math.max(0.25, t.speed_factor || 1);
  const totalDuration = tracks.reduce((sum, t) => sum + getEffDur(t), 0);

  // ── Part B: audio segment playback ──
  // Resolve each audio segment to a playable URL (asset source_url or inline).
  const assetUrlById = useMemo(() => {
    const m = {};
    (assets || []).forEach(a => { if (a && (a.asset_id || a.id)) m[a.asset_id || a.id] = a.source_url || a.url; });
    return m;
  }, [assets]);
  const playableAudio = useMemo(() => (audioSegments || [])
    .map(s => ({ ...s, _url: s.source_url || s.url || s.audio_url || assetUrlById[s.asset_id] || null }))
    .filter(s => s._url), [audioSegments, assetUrlById]);
  const audioElsRef = useRef({});

  useEffect(() => {
    const els = audioElsRef.current;
    playableAudio.forEach(s => {
      if (!els[s.id]) {
        const a = new Audio();
        a.preload = 'auto'; a.crossOrigin = 'anonymous'; a.src = s._url;
        els[s.id] = a;
      } else if (els[s.id].dataset ? els[s.id]._src !== s._url : false) {
        els[s.id].src = s._url; els[s.id]._src = s._url;
      }
    });
    Object.keys(els).forEach(id => {
      if (!playableAudio.find(s => s.id === id)) { try { els[id].pause(); } catch (_) {} delete els[id]; }
    });
  }, [playableAudio]);

  useEffect(() => () => {
    Object.values(audioElsRef.current).forEach(a => { try { a.pause(); } catch (_) {} });
    audioElsRef.current = {};
  }, []);

  const syncAudio = useCallback((e, isPlayingNow) => {
    playableAudio.forEach(s => {
      const a = audioElsRef.current[s.id]; if (!a) return;
      const start = s.start_time || 0;
      const dur = s.duration || 0;
      const inRange = e >= start && e < start + dur;
      a.volume = Math.max(0, Math.min(1, (s.volume == null ? 100 : s.volume) / 100)) * (s.muted ? 0 : 1);
      if (isPlayingNow && inRange) {
        const target = (s.trim_start || 0) + (e - start);
        if (Math.abs(a.currentTime - target) > 0.3) { try { a.currentTime = target; } catch (_) {} }
        if (a.paused) a.play().catch(() => {});
      } else if (!a.paused) {
        a.pause();
      }
    });
  }, [playableAudio]);

  const pauseAllAudio = useCallback(() => {
    Object.values(audioElsRef.current).forEach(a => { try { a.pause(); } catch (_) {} });
  }, []);

  // ── Part B: active video clip sync (trim / speed / scrub) ──
  const clipStartAcc = useCallback((idx) => {
    let acc = 0;
    for (let i = 0; i < idx; i++) acc += getEffDur(tracks[i]);
    return acc;
  }, [tracks]);

  const syncVideo = useCallback((e, isPlayingNow) => {
    const v = videoRef.current;
    const t = tracks[currentIdxRef.current];
    if (!v || !t || t.type !== 'video') return;
    v.muted = !t.audio_enabled;
    const speed = Math.max(0.25, t.speed_factor || 1);
    v.playbackRate = speed;
    const local = Math.max(0, e - clipStartAcc(currentIdxRef.current));
    const target = (t.trim_start || 0) + local * speed;
    if (Math.abs(v.currentTime - target) > 0.3) { try { v.currentTime = target; } catch (_) {} }
    if (isPlayingNow) { if (v.paused) v.play().catch(() => {}); }
    else if (!v.paused) v.pause();
  }, [tracks, clipStartAcc]);

  const stop = useCallback(() => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    timerRef.current = null;
    setPlaying(false);
    pauseAllAudio();
    const v = videoRef.current; if (v && !v.paused) v.pause();
    if (onPlayingChange) onPlayingChange(false);
  }, [onPlayingChange, pauseAllAudio]);

  // Start (or resume) the clock from the current elapsed position.
  const play = useCallback(() => {
    setPlaying(true);
    if (onPlayingChange) onPlayingChange(true);
    setAssetSelected(false);
    // Resume from the current playhead; if we're already at/after the end, restart.
    let from = elapsedRef.current;
    if (from >= totalDuration - 0.01) from = 0;
    startElapsedRef.current = from;
    elapsedRef.current = from;
    lastUiRef.current = from;
    startWallRef.current = performance.now();
    syncAudio(from, true);
    syncVideo(from, true);

    const tick = () => {
      const e = startElapsedRef.current + (performance.now() - startWallRef.current) / 1000;
      elapsedRef.current = e;

      let acc = 0, idx = 0, ended = false;
      for (let i = 0; i < tracks.length; i++) {
        acc += getEffDur(tracks[i]);
        if (e < acc) { idx = i; break; }
        if (i === tracks.length - 1 && e >= acc) { ended = true; }
      }
      if (ended) { elapsedRef.current = totalDuration; setElapsed(totalDuration); if (onTimeUpdate) onTimeUpdate(totalDuration); stop(); return; }

      if (idx !== currentIdxRef.current) {
        currentIdxRef.current = idx;
        setCurrentIdx(idx);
      }
      syncAudio(e, true);
      syncVideo(e, true);
      if (e - lastUiRef.current >= 0.1) {
        lastUiRef.current = e;
        setElapsed(e);
        if (onTimeUpdate) onTimeUpdate(e);
      }
      timerRef.current = requestAnimationFrame(tick);
    };
    timerRef.current = requestAnimationFrame(tick);
  }, [tracks, stop, onTimeUpdate, onPlayingChange, totalDuration, syncAudio, syncVideo]);

  const togglePlay = useCallback(() => { if (playing) stop(); else play(); }, [playing, stop, play]);

  const resetToStart = useCallback(() => {
    stop();
    setCurrentIdx(0); currentIdxRef.current = 0;
    setElapsed(0); elapsedRef.current = 0; lastUiRef.current = 0;
    setAssetSelected(false);
    syncAudio(0, false);
    if (onTimeUpdate) onTimeUpdate(0);
  }, [stop, syncAudio, onTimeUpdate]);

  useEffect(() => { return () => { if (timerRef.current) cancelAnimationFrame(timerRef.current); }; }, []);

  // Space bar toggles play/pause unless focus is in a text field / inline editor.
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.code !== 'Space' && ev.key !== ' ') return;
      const el = ev.target;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      ev.preventDefault();
      togglePlay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay]);

  // Scrubbing on the timeline: move clock + audio + video to the new time.
  useEffect(() => {
    if (playing || seekTime == null) return;
    const clamped = Math.max(0, Math.min(totalDuration, seekTime));
    elapsedRef.current = clamped;
    lastUiRef.current = clamped;
    setElapsed(clamped);
    let acc = 0, idx = tracks.length ? tracks.length - 1 : 0;
    for (let i = 0; i < tracks.length; i++) {
      acc += getEffDur(tracks[i]);
      if (clamped < acc) { idx = i; break; }
    }
    setCurrentIdx(idx); currentIdxRef.current = idx;
    syncAudio(clamped, false);
    // Seek the active video (if any) to the scrubbed position without playing.
    requestAnimationFrame(() => syncVideo(clamped, false));
  }, [seekTime, tracks, playing, totalDuration, syncAudio, syncVideo]);

  useEffect(() => { if (selectedLayerId) setAssetSelected(false); }, [selectedLayerId]);

  const currentTrack = tracks[currentIdx] || null;
  const activeLayers = allLayers.filter(l => elapsed >= (l.start || 0) && elapsed <= (l.end || 999));

  const handleAssetSelect = useCallback(() => {
    setAssetSelected(true);
    if (onSelectLayer) onSelectLayer(null);
  }, [onSelectLayer]);

  const handleAssetTransform = useCallback((t) => {
    if (onUpdateTrackTransform) onUpdateTrackTransform(currentIdx, t);
  }, [onUpdateTrackTransform, currentIdx]);

  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage()) {
      setAssetSelected(false);
      if (onSelectLayer) onSelectLayer(null);
    }
  }, [onSelectLayer]);

  const handleWheel = useCallback((e) => {
    if (!assetSelected || playing) return;
    e.evt.preventDefault();
    const delta = e.evt.deltaY;
    const currentTransform = tracks[currentIdx]?.transform || {};
    const currentScale = currentTransform.scale || 1;
    const factor = delta > 0 ? 0.95 : 1.05;
    const newScale = Math.min(5, Math.max(0.3, currentScale * factor));
    if (onUpdateTrackTransform) {
      onUpdateTrackTransform(currentIdx, { ...currentTransform, scale: parseFloat(newScale.toFixed(4)) });
    }
  }, [assetSelected, playing, tracks, currentIdx, onUpdateTrackTransform]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setAssetSelected(false);
      if (onSelectLayer) onSelectLayer(null);
    }
  };

  const isPhotoTrack = currentTrack?.source_url && currentTrack.type !== 'video';
  const currentZoom = Math.round((currentTrack?.transform?.scale || 1) * 100);
  const currentRotation = Math.round(currentTrack?.transform?.rotation || 0);

  const setZoom = useCallback((newPct) => {
    const clamped = Math.min(500, Math.max(30, newPct));
    const t = tracks[currentIdx]?.transform || {};
    if (onUpdateTrackTransform) onUpdateTrackTransform(currentIdx, { ...t, scale: parseFloat((clamped / 100).toFixed(4)) });
  }, [tracks, currentIdx, onUpdateTrackTransform]);

  const resetTransform = useCallback(() => {
    if (onUpdateTrackTransform) onUpdateTrackTransform(currentIdx, { x: 0, y: 0, scale: 1, rotation: 0 });
  }, [currentIdx, onUpdateTrackTransform]);

  return (
    <div ref={rootRef} className="flex flex-col items-center justify-center relative w-full h-full" data-testid="preview-player">
      {/* 9:16 Preview Canvas (sizes to available space) */}
      <div
        className="relative bg-black rounded-lg overflow-hidden shadow-xl select-none"
        style={{ width: canvasW, height: canvasH }}
        data-canvas-root
      >
        {isPhotoTrack ? (
          <Stage
            width={canvasW} height={canvasH}
            onClick={handleStageClick} onTap={handleStageClick}
            onWheel={handleWheel}
            style={{ position: 'absolute', top: 0, left: 0 }}
            data-testid="konva-viewport"
          >
            <Layer>
              <AssetImage
                track={currentTrack}
                isPlaying={playing}
                isSelected={assetSelected}
                onSelect={handleAssetSelect}
                onTransformChange={handleAssetTransform}
                canvasW={canvasW} canvasH={canvasH} sx={sx} sy={sy}
              />
            </Layer>
          </Stage>
        ) : currentTrack?.source_url ? (
          <video
            ref={videoRef}
            key={currentTrack.source_url}
            src={currentTrack.source_url}
            className="w-full h-full object-cover"
            muted={!currentTrack.audio_enabled}
            playsInline
            preload="auto"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">
            {tracks.length === 0 ? 'Add clips to preview' : 'No preview'}
          </div>
        )}

        {/* HTML overlay: layers + HUD */}
        <div
          className="absolute inset-0"
          style={{ pointerEvents: 'none', zIndex: 10 }}
          onClick={handleOverlayClick}
        >
          {activeLayers.map((layer) => (
            <InteractiveLayer
              key={layer.id}
              layer={layer}
              isSelected={selectedLayerId === layer.id}
              onSelect={(id) => { setAssetSelected(false); if (onSelectLayer) onSelectLayer(id); }}
              onUpdate={onUpdateLayer || (() => {})}
              onUpdateLive={onUpdateLayerLive || onUpdateLayer || (() => {})}
              onGestureStart={onLayerGestureStart}
              propertyData={propertyData}
              isPlaying={playing}
              sx={sx} sy={sy}
            />
          ))}

          {assetSelected && !playing && (
            <div className="absolute top-2 right-2 z-30 bg-purple-600/80 backdrop-blur-sm rounded px-1.5 py-0.5 text-white text-[10px] pointer-events-none" data-testid="asset-adjust-badge">
              {currentZoom}%{currentRotation !== 0 ? ` · ${currentRotation}°` : ''}
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-30 pointer-events-none">
            <div className="h-full bg-purple-500 transition-all" style={{ width: totalDuration > 0 ? `${(elapsed / totalDuration) * 100}%` : '0%' }} />
          </div>

          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-1 z-30 pointer-events-none">
            {tracks.map((_, idx) => (
              <div key={idx} className={`w-1.5 h-1.5 rounded-full ${currentIdx === idx ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Zoom slider — visible when asset is selected */}
      {assetSelected && !playing && isPhotoTrack && (
        <div className="flex items-center gap-2 mt-2 w-full" style={{ maxWidth: canvasW }} data-testid="zoom-slider-bar">
          <button className="p-0.5 rounded hover:bg-muted/60 text-muted-foreground" onClick={() => setZoom(currentZoom - 10)} data-testid="zoom-out-btn">
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <input
            type="range" min={30} max={500} step={1} value={currentZoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1 accent-purple-600 cursor-pointer"
            data-testid="zoom-slider"
          />
          <button className="p-0.5 rounded hover:bg-muted/60 text-muted-foreground" onClick={() => setZoom(currentZoom + 10)} data-testid="zoom-in-btn">
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <span className="text-[10px] text-muted-foreground w-8 text-right tabular-nums">{currentZoom}%</span>
          <button className="p-0.5 rounded hover:bg-muted/60 text-muted-foreground" onClick={resetTransform} title="Reset transform" data-testid="zoom-reset-btn">
            <RotateCw className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3 mt-3">
        <Button variant="ghost" size="icon" className="h-8 w-8"
          onClick={resetToStart}
          data-testid="preview-reset-btn">
          <SkipBack className="h-4 w-4" />
        </Button>
        <Button
          variant={playing ? 'secondary' : 'default'} size="sm"
          onClick={togglePlay} disabled={tracks.length === 0}
          data-testid="preview-play-btn">
          {playing ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
          {playing ? 'Pause' : 'Play'}
        </Button>
        <span className="text-xs text-muted-foreground">{elapsed.toFixed(1)}s / {totalDuration.toFixed(1)}s</span>
      </div>
    </div>
  );
};
